/**
 * xlsxChartParse — extract native Excel charts from .xlsx bytes for in-app
 * rendering (Jun 24, 2026, Fabio's "grafico non si vede").
 *
 * Why this exists: the in-app spreadsheet editor uses Univer's OSS core preset,
 * which has NO chart plugin (charts are a Univer-Pro/commercial feature). The
 * BE generates a perfectly valid native chart in the .xlsx (visible in Excel/
 * Sheets/download), but Univer drops it on import. Rather than license Pro, we
 * parse the chart definition + its CACHED data (the exact values Excel renders)
 * here and re-draw it with the existing ECharts `<ChartRaw>` wrapper, anchored
 * on the sheet via a Univer canvas-popup.
 *
 * Pure + side-effect-free (only JSZip + DOMParser) → unit-testable without a
 * Univer/React/ECharts runtime. Namespace-aware: OOXML parts use prefixed tags
 * (`<c:ser>`, `<xdr:from>`), so every lookup is by *localName*, never a raw
 * prefixed string — the lesson from the chart-anchor incident.
 */

import JSZip from "jszip";

export type SheetChartType = "bar" | "line" | "area" | "pie" | "scatter";

export interface ParsedSheetChartSeries {
  readonly name: string;
  readonly values: number[];
}

export interface ParsedSheetChart {
  /** Worksheet the chart is anchored to (matches Univer `getSheetByName`). */
  readonly sheetName: string;
  /** 0-indexed anchor cell (top-left ``<xdr:from>``). */
  readonly row: number;
  readonly col: number;
  readonly type: SheetChartType;
  readonly title?: string;
  readonly categories: string[];
  readonly series: ParsedSheetChartSeries[];
}

// --- small namespace-agnostic DOM helpers ---------------------------------
const allLocal = (root: Element | Document, name: string): Element[] =>
  Array.from(root.getElementsByTagName("*")).filter((e) => e.localName === name);

const firstLocal = (root: Element | Document, name: string): Element | null =>
  allLocal(root, name)[0] ?? null;

/** Ordered ``<c:pt idx="n"><c:v>..</c:v></c:pt>`` cache values (sorted by idx). */
function cachePoints(cacheParent: Element | null): string[] {
  if (!cacheParent) return [];
  const pts = allLocal(cacheParent, "pt")
    .map((pt) => ({
      idx: Number(pt.getAttribute("idx") ?? "0"),
      v: firstLocal(pt, "v")?.textContent ?? "",
    }))
    .sort((a, b) => a.idx - b.idx);
  return pts.map((p) => p.v);
}

/** Resolve an OOXML relationship Target against a part's base dir. */
function normTarget(baseDir: string, target: string): string {
  if (target.startsWith("/")) return target.slice(1);
  let base = baseDir;
  let t = target;
  while (t.startsWith("../")) {
    t = t.slice(3);
    base = base.replace(/[^/]+\/$/, "");
  }
  return base + t;
}

function detectType(plotArea: Element): SheetChartType {
  const has = (n: string) => firstLocal(plotArea, n) != null;
  if (has("pieChart") || has("doughnutChart") || has("pie3DChart")) return "pie";
  if (has("lineChart")) return "line";
  if (has("areaChart")) return "area";
  if (has("scatterChart")) return "scatter";
  return "bar"; // barChart / bar3DChart / fallback
}

function parseChartXml(xml: string): Omit<ParsedSheetChart, "sheetName" | "row" | "col"> | null {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  if (doc.getElementsByTagName("parsererror").length) return null;
  const plotArea = firstLocal(doc, "plotArea");
  if (!plotArea) return null;
  const type = detectType(plotArea);

  const title = firstLocal(firstLocal(doc, "title") ?? doc, "rich")
    ? allLocal(firstLocal(doc, "title")!, "t").map((t) => t.textContent ?? "").join("").trim()
    : undefined;

  const serEls = allLocal(plotArea, "ser");
  const series: ParsedSheetChartSeries[] = [];
  let categories: string[] = [];
  serEls.forEach((ser, i) => {
    // series name: <c:tx> → strCache pt, or a direct <c:v>
    const tx = firstLocal(ser, "tx");
    const name =
      (tx ? cachePoints(firstLocal(tx, "strCache"))[0] ?? firstLocal(tx, "v")?.textContent : "") ||
      `Serie ${i + 1}`;
    // values: <c:val> → numCache
    const valEl = firstLocal(ser, "val");
    const values = cachePoints(
      firstLocal(valEl ?? ser, "numCache"),
    ).map((v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    });
    // categories (shared): first series with a <c:cat> wins
    if (!categories.length) {
      const catEl = firstLocal(ser, "cat");
      if (catEl) {
        categories =
          cachePoints(firstLocal(catEl, "strCache")) .length
            ? cachePoints(firstLocal(catEl, "strCache"))
            : cachePoints(firstLocal(catEl, "numCache"));
      }
    }
    series.push({ name: String(name), values });
  });
  if (!series.length) return null;
  // synthesize categories when the chart omitted them
  if (!categories.length) {
    const n = Math.max(...series.map((s) => s.values.length), 0);
    categories = Array.from({ length: n }, (_, i) => String(i + 1));
  }
  return { type, title, categories, series };
}

function parseAnchor(drawingXml: string): { row: number; col: number } | null {
  const doc = new DOMParser().parseFromString(drawingXml, "application/xml");
  const from = firstLocal(doc, "from");
  if (!from) return null;
  const col = Number(firstLocal(from, "col")?.textContent ?? "0");
  const row = Number(firstLocal(from, "row")?.textContent ?? "0");
  return { row: Number.isFinite(row) ? row : 0, col: Number.isFinite(col) ? col : 0 };
}

const XML_ENT: Record<string, string> = {
  "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"', "&apos;": "'",
};
const decodeXml = (s: string): string =>
  s.replace(/&(amp|lt|gt|quot|apos);/g, (m) => XML_ENT[m] ?? m);

/**
 * Parse every native chart in an .xlsx, resolving each to its host sheet name,
 * anchor cell and cached data. Returns ``[]`` on any failure (no-throw) so the
 * editor never breaks when a workbook has no/odd charts.
 */
export async function parseXlsxCharts(
  bytes: ArrayBuffer | Uint8Array,
): Promise<ParsedSheetChart[]> {
  try {
    const zip = await JSZip.loadAsync(bytes);
    const names = Object.keys(zip.files);
    const read = async (p: string): Promise<string> =>
      (await zip.file(p)?.async("string")) ?? "";

    // drawing part -> host worksheet part (worksheet rels)
    const drawingToSheet: Record<string, string> = {};
    for (const n of names) {
      const m = n.match(/^xl\/worksheets\/_rels\/(sheet\d+\.xml)\.rels$/);
      if (!m) continue;
      const rels = await read(n);
      for (const tm of rels.matchAll(/Target="([^"]*drawing\d+\.xml)"/g)) {
        drawingToSheet[normTarget("xl/worksheets/", tm[1] ?? "")] = `xl/worksheets/${m[1]}`;
      }
    }

    // sheet part -> sheet name (workbook + workbook rels, attr-order agnostic)
    const wb = await read("xl/workbook.xml");
    const wbRels = await read("xl/_rels/workbook.xml.rels");
    const ridToTarget: Record<string, string> = {};
    for (const rm of wbRels.matchAll(/<Relationship\b([^>]*)>/g)) {
      const attrs = rm[1] ?? "";
      const id = attrs.match(/Id="([^"]+)"/)?.[1];
      const tgt = attrs.match(/Target="([^"]+)"/)?.[1];
      if (id && tgt) ridToTarget[id] = normTarget("xl/", tgt);
    }
    const sheetPartToName: Record<string, string> = {};
    for (const sm of wb.matchAll(/<(?:\w+:)?sheet\b([^>]*)\/?>/g)) {
      const attrs = sm[1] ?? "";
      const nm = attrs.match(/name="([^"]+)"/)?.[1];
      const rid = attrs.match(/r:id="([^"]+)"/)?.[1];
      if (nm && rid && ridToTarget[rid]) sheetPartToName[ridToTarget[rid]] = decodeXml(nm);
    }

    const out: ParsedSheetChart[] = [];
    for (const n of names) {
      if (!/\/charts?\/chart\d+\.xml$/.test(n)) continue;
      const chartFile = n.split("/").pop()!;
      // find the drawing that references this chart + its anchor
      let anchor = { row: 1, col: 0 };
      let sheetName = "";
      for (const dn of names) {
        if (!/^xl\/drawings\/drawing\d+\.xml$/.test(dn)) continue;
        const drelsPath = `xl/drawings/_rels/${dn.split("/").pop()}.rels`;
        const drels = await read(drelsPath);
        if (!drels.includes(chartFile)) continue;
        anchor = parseAnchor(await read(dn)) ?? anchor;
        sheetName = sheetPartToName[drawingToSheet[dn] ?? ""] ?? "";
        break;
      }
      const spec = parseChartXml(await read(n));
      if (!spec) continue;
      // Defensive clamp: an off-screen anchor (pre-BE-fix files at col 100/
      // row 50) would hide the popup too — pull it back near the origin.
      const col = anchor.col > 40 ? 0 : anchor.col;
      const row = anchor.row > 400 ? 1 : anchor.row;
      out.push({ ...spec, sheetName, row, col });
    }
    return out;
  } catch {
    return [];
  }
}
